import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
    HttpResponse
} from '@angular/common/http';
import { Observable, throwError, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ErrorHandlerService } from '../error-handler.service';
import { ToastService } from 'src/app/core/services/toast.service';
import { AuthService } from '../auth.service';
import { API_END_POINTS } from '../../constants/types.constants';
import { environment } from '../../../../environments/environment';
import { ErrorInfo } from '../../models/notification.model';

const API_ERROR_MSGS_JSON: { [key in API_END_POINTS]: string } = {
    'news/get-new-by-coins': `Unable to fetch the news at this moment. Please try again later.`,
    'coin-info/get-coin-details': `Unable to retrieve the coin detail. Please try again later.`,
    'coin-info/get-coin-info-list': `Sorry, we are unable to fetch the coin list at the moment. Please try again later.`,
    'coin-info/get-marquee-coins': `Sorry, we are unable to fetch the marquee coins at the moment. Please try again later.`,
    'coin-info/get-trending-coins': `Sorry, we are unable to fetch the trending coins at the moment. Please try again later.`,
    'coin-info/get-coin-categories': `Sorry, we are unable to fetch categories at the moment. Please try again later.`,
    'coin-info/get-coin-price-history': `Sorry, we are unable to fetch coin historical data at the moment. Please try again later.`,
    'coin-pairs/pairs': `Sorry, we are unable to fetch the coin pairs at this moment. Please try again later.`,
    'coin-pairs/market-pairs-v2': `Sorry, we are unable to fetch the coin market pairs at this moment. Please try again later.`,
    'api/public-api/get-wallets-pairs': `Sorry, we are unable to fetch the coin wallet pairs at this moment. Please try again later.`,
    'landing-page/coin-info-id': `Unable to fetch Coin Info`,
    'api/public-api/coin-price-id': `Unable to fetch Coin Prices`
};

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
    constructor(
        private errorService: ErrorHandlerService,
        private router: Router,
        private toastService: ToastService,
        private authService: AuthService
    ) {}
    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        return next.handle(request).pipe(
            /* Tap all Incoming and Outgoing HTTP Events */
            tap(response => {
                /* Filter for HTTP Response Event Objects */
                if (response instanceof HttpResponse) {
                    /**
                     * Filter for HTTP Response Objects' body which have a `status` property
                     * Filter for HTTP Response Objects' body whose status is not 200
                     */
                    if (response.body.status && response.body.status === 451) {
                        window.location.href = environment.IXFI_EXCHANGE + 'home/welcome';
                    } else if (response.body.status === 503) {
                        this.authService.logout();
                        // localStorage.setItem('Retry-After', JSON.stringify(response.body['Retry-After']));
                        this.router.navigate(['/server-management']);
                    } else if (response.body.status === 444) {
                        window.location.href = environment.IXFI_EXCHANGE + 'auth/ixfi-pin';
                    }
                }
            }),
            catchError((error: HttpErrorResponse) => {
                console.log(error);
                this._handleErrorResponse(error);
                return throwError(() => error);
            })
        );
    }

    private _handleErrorResponse(error: HttpErrorResponse) {
        if (error?.error instanceof HttpErrorResponse) {
            const errorInfo: ErrorInfo = {
                errorCode: error.status,
                errorTextOne: error.error.message,
                errorTextTwo: 'TRY_AGAIN_IN_FEW_MINUTES'
            };
            this.navigateToErrorPage(errorInfo);
            /* Client Side Error */
        } else {
            if (!error.url?.includes('i18n')) {
                /* Server Side Error */
                switch (error.status) {
                    case 0:
                        this.systemUnavailable();
                        break;
                    case 401:
                    case 419:
                        this.authenticationExpired();
                        break;
                    case 404:
                    case 500:
                    case 501:
                        this.resourceNotFound(error);
                        break;
                    case 503:
                        localStorage.setItem('Retry-After', error.error['Retry-After']);
                        this.authService.logout();
                        this.router.navigate(['/server-management']);
                        break;
                    case 451:
                        window.location.href = environment.IXFI_EXCHANGE + 'home/welcome';
                        break;
                    case 444:
                        window.location.href = environment.IXFI_EXCHANGE + 'auth/ixfi-pin';
                        break;
                    default:
                        this.defaultErrorStatus();
                        break;
                }

                // Will be refactored later
                // const errorCodeJson: { [key: number | string]: Function } = {
                //     0: this.systemUnavailable,
                //     401: this.authenticationExpired,
                //     419: this.authenticationExpired,
                //     404: this.resourceNotFound,
                //     500: this.internalServerError,
                //     501: this.internalServerError,
                //     503: (): void => {

                //     },
                //     default: this.defaultErrorStatus
                // };
                // (errorCodeJson[error.status] || errorCodeJson['default'])(error);
            }
        }
    }

    systemUnavailable = (): void => {
        const errorInfo: ErrorInfo = {
            errorCode: 0,
            errorTextOne: 'STATUS.0',
            errorTextTwo: 'TRY_AGAIN_IN_FEW_MINUTES'
        };
        this.navigateToErrorPage(errorInfo);
    };

    authenticationExpired = (): void => {
        const errorInfo: ErrorInfo = {
            errorCode: 419,
            errorTextOne: 'STATUS.419',
            errorTextTwo: 'AUTH_TOKEN_EXPIRED'
        };
        this.navigateToErrorPage(errorInfo);
        this.authService.logout();
    };

    resourceNotFound = (error: HttpErrorResponse): void => {
        const endPoint = error.url?.split('api/front/')[1] || '';
        if (Object.keys(API_ERROR_MSGS_JSON).includes(endPoint)) {
            this.toastService.open({
                toastType: 'error',
                toastTitle: `${error.status} - ${error.message}`,
                toastMessage: API_ERROR_MSGS_JSON[endPoint]
            });
        }
    };

    internalServerError = (): void => {
        const errorInfo: ErrorInfo = {
            errorCode: 500,
            errorTextOne: 'STATUS.500',
            errorTextTwo: 'SERVER_COULD_NOT_PROCEED_REQUEST'
        };
        this.navigateToErrorPage(errorInfo);
    };

    defaultErrorStatus = () => {
        this.toastService.open({
            toastType: 'error',
            toastTitle: 'An Unknown Error Occurred',
            toastMessage: 'Looks like something went wrong. Please contact the administrator.'
        });
    };

    navigateToErrorPage(errorInfo: ErrorInfo) {
        this.errorService.updateErrorMessage(errorInfo);
        this.router.navigate(['/page-not-found']);
    }
}
